import { isOrgBookKey } from "../../../../../../../hooks/contacts/contactBookRules";
import type { RealEstateContact } from "../../../../../../../@types/sharedSchema";

/**
 * MAY THIS VIEWER EDIT THIS CONTACT IN PLACE?
 *
 * ── Own records only, and that is a product decision, not a limitation ───────────
 *
 * The סקירה tab has two hosts. On the full contact page the document always comes from
 * `useLiveContact`, which reads the VIEWER's own subcollection — so it is always theirs.
 * In the CRM side panel it can also be a colleague's, hydrated once through
 * `/contacts/get-details`, and that branch has no live listener and no refetch verb: a
 * value edited there would save correctly and the chip would keep showing the old one
 * until the panel was closed and reopened. A save that looks like it did nothing is worse
 * than no affordance at all.
 *
 * A colleague's record has its own destination now — the agency view of the person — so
 * in practice nothing is lost by keeping the inline editors off it.
 *
 * ⚠️ THE FALLBACK IS A READ SURFACE, NOT A WORKING EDITOR. Where a trigger still exists
 * for an unowned record it opens `EditContactDialog`, and that dialog sends neither an
 * owner nor `view: "org"` — so a save from it addresses the CALLER's own subcollection.
 * For a colleague's contact that is either a 404 or, worse, a write onto the caller's own
 * copy of the same phone number. Do not describe this path as "edit it in the dialog
 * instead"; the honest reading is that an unowned record is not editable from this tab at
 * all, and the dialog is there to show the fields the panel does not.
 *
 * Closing that properly means giving `EditContactDialog` the same owner-and-scope
 * treatment `CrmCellEditor` already has. It is out of scope here precisely because it is
 * a change to a surface eight other screens open.
 *
 * ⚠️ THIS IS A UI DECISION AND NOT AN ACCESS CONTROL. The server resolves scope on every
 * write, intersecting the requested owner with the caller's own resolved membership; it
 * does not care what this function returned. Nothing here may be relied on to keep anyone
 * out of anything — its only job is to avoid offering an affordance whose result the
 * viewer would not see.
 *
 * ── The pooled book (plan D4) ──────────────────────────────────────────────────────
 *
 * A member of a pooled agency reads the OFFICE's record. The server's rule for editing one
 * is "on its holder list, or the full pool" (`pooledBookRules.ts` there), and for the
 * documents the server really serves this function can see NEITHER half:
 *
 *  - the holder list is not ON the document. The server answers `holderIds` BESIDE `doc`
 *    (`CrmContactGetResponse`) and the document store keeps the document and its version
 *    only (`fetchContactDocument`). The branch below still honours a `holderIds` a caller
 *    stamps on what it passes; no caller does today.
 *  - `userId` is absent on every office record: the merge drops it with the other
 *    owner-scoped fields, and the Cloud Function that used to stamp it never runs for a
 *    record that lives in Postgres.
 *
 * So for a pooled member this answers TRUE for every office record. That is right for the
 * two viewers the server accepts — a holder, and an admin with the full pool who does not
 * hold the record (so that viewer is NOT denied the editors) — and it is an editor that will
 * be refused, one save at a time and in the server's own sentence, for a member below the
 * full pool on a record they do not hold. It is the side to be wrong on while the holder
 * list is out of reach: answering false without it would take the editors away from every
 * holder.
 *
 * ⚠️ A KNOWN GAP, NOT A DECISION. Closing it takes the holder list on what the two panels
 * are handed, and the permission beside it (`useCrmMyAccess` is a hook call away, cached
 * and persisted — that half was never the obstacle). `contactEditAccess.test.ts` pins what
 * happens today so the day the list arrives, the change is made on purpose.
 */
export function canInlineEditContact(
  contact: RealEstateContact & { holderIds?: string[] },
  viewerEmail: string | null | undefined,
  viewerBookKey?: string | null
): boolean {
  const viewer = (viewerEmail ?? "").trim().toLowerCase();
  const owner = (contact.userId ?? "").trim().toLowerCase();
  if (isOrgBookKey(viewerBookKey)) {
    if ((contact.holderIds ?? []).some((holder) => holder.trim().toLowerCase() === viewer)) return true;
    return !owner || owner === (viewerBookKey as string).trim().toLowerCase();
  }
  // No stamped owner means the document was never through the Cloud Function that adds
  // one; on the full page that document came out of the viewer's own subcollection, so it
  // is theirs by construction.
  if (!owner) return true;
  return Boolean(viewer) && owner === viewer;
}
